import maplibregl, { LayerSpecification, Map as MlMap, PaddingOptions } from "maplibre-gl";
import type { LngLat, TileLayerName } from "../types";
import { addBackgroundTileLayer, captureAndStripStyleLayers, DEFAULT_STYLE, removeBackgroundTileLayer } from "./backgroundTileLayer";
import {
  drawMapCanvasInto,
  waitForMapIdle,
} from "./mapSnapshot";

export interface BackgroundMapCamera {
  center: LngLat;
  zoom: number;
  bearing: number;
  pitch: number;
  /** Voir `MapLibreAdapter`'s `"move"` handler : sans ça, un `padding` posé par
   * `ensureShapeVisible` sur la carte principale (pour garder une forme visible malgré la sidebar)
   * désynchroniserait visuellement les formes de ce fond OSM — `Map#getCenter()` renvoie le point
   * centré dans la zone *rognée* par le padding, pas le centre géométrique réel du conteneur. */
  padding: PaddingOptions;
}

/**
 * Carte MapLibre secondaire, non interactive, dédiée UNIQUEMENT à peindre `TileLayerName` — voir
 * `MapLibreAdapter.updateBackgroundMode` : activée quand au moins une forme-image SVG est affichée
 * (`<img>` DOM derrière le canvas de la carte principale) ET que `tileLayerName !== "unset"`, cas où
 * la carte principale ne peut plus peindre son propre fond sans masquer ce SVG. Son conteneur est
 * inséré en tout premier enfant du conteneur racine passé à `MapLibreAdapter.init()` (donc AVANT le
 * wrapper `.maplibregl-map` que la carte principale y crée elle-même, lequel héberge à la fois
 * `imageOverlayContainer` et son propre canvas) — visuellement tout en bas, sans besoin de z-index
 * puisque rien dans cette chaîne n'en déclare.
 */
export class BackgroundMap {
  private readonly container: HTMLDivElement;
  private readonly map: MlMap;
  /** Décidés par `MapLibreAdapter` : cette carte suit les calques comme elle suit la caméra, elle
   * ne télécharge ni ne filtre rien. */
  private osmLayers: LayerSpecification[];
  /** À repasser tel quel à `removeBackgroundTileLayer` — `osmLayers` peut avoir changé sans avoir
   * encore été appliqué (voir `ready`). */
  private paintedOsmLayers: LayerSpecification[] = [];
  private tileLayerName: TileLayerName;
  /** `paintedOsmLayers` ne suffit pas à savoir quoi retirer : le fond satellite n'a pas de calques de
   * style, seulement ses propres id. */
  private paintedTileLayerName: TileLayerName = "unset";
  private readonly ready: Promise<void>;

  constructor(
    root: HTMLElement,
    tileLayerName: TileLayerName,
    osmLayers: LayerSpecification[],
    camera: BackgroundMapCamera,
    pixelRatio: number,
  ) {
    this.tileLayerName = tileLayerName;
    this.osmLayers = osmLayers;

    this.container = document.createElement("div");
    this.container.className = "background-map-container";
    this.container.style.position = "absolute";
    this.container.style.inset = "0";
    this.container.style.pointerEvents = "none";
    root.prepend(this.container);

    this.map = new maplibregl.Map({
      container: this.container,
      style: DEFAULT_STYLE,
      center: camera.center,
      zoom: camera.zoom,
      bearing: camera.bearing,
      pitch: camera.pitch,
      pixelRatio,
      attributionControl: false,
      // Ne doit jamais capter de clic/drag — toute l'interaction reste sur la carte principale.
      interactive: false,
      dragRotate: false,
      // PAS de `maxPitch: 0` ici (contrairement à une version antérieure) : cette carte est purement
      // suiveuse (`syncCamera`/`jumpTo`, jamais interactive) — son pitch ne vient QUE de la carte
      // principale, déjà bornée par `MapLibreAdapter.allowPitch`/`MAX_PITCH_DEG`. La brider
      // séparément ici clampait silencieusement `jumpTo({ pitch })` à 0 dès que la principale
      // s'inclinait (mode 3D + fond OSM/satellite actif), désynchronisant visuellement le fond
      // (resté plat) des formes/du SVG (inclinés) — flagrant pendant un pinch-zoom, où les deux
      // cartes rejouent alors des trajectoires de caméra différentes. Le plafond par défaut de
      // MapLibre (60°) suffit, il correspond déjà à `MAX_PITCH_DEG`.
    });
    // `padding` n'existe pas dans les options du constructeur (contrairement à `center`/`zoom`/
    // `bearing`/`pitch`) — seul `Map#setPadding` (ou `jumpTo`/`easeTo`) peut le poser.
    this.map.setPadding(camera.padding);

    this.ready = new Promise<void>((resolve) => {
      this.map.on("load", () => {
        captureAndStripStyleLayers(this.map);
        this.repaint();
        resolve();
      });
    });
  }

  private repaint(): void {
    removeBackgroundTileLayer(this.map, this.paintedTileLayerName, this.paintedOsmLayers);
    addBackgroundTileLayer(this.map, this.tileLayerName, this.osmLayers);
    this.paintedTileLayerName = this.tileLayerName;
    this.paintedOsmLayers = this.osmLayers;
  }

  /** Change le fond affiché SANS recréer l'instance (voir `MapLibreAdapter.updateBackgroundMode` :
   * `tileLayerName` peut changer, ex. `"osm"` → `"satellite"`, pendant que ce mode reste actif).
   * Diffère jusqu'à `"load"` si appelé avant (rien n'est encore peint). */
  setTileLayerName(name: TileLayerName): void {
    if (this.tileLayerName === name) return;
    this.tileLayerName = name;
    void this.ready.then(() => this.repaint());
  }

  /** Mémorisés même sous un autre fond, pour le retour. */
  setOsmLayers(layers: LayerSpecification[]): void {
    this.osmLayers = layers;
    void this.ready.then(() => {
      if (this.tileLayerName === "osm") this.repaint();
    });
  }

  /** Réplique instantanément la caméra de la carte principale — voir le listener `"move"` unique de
   * `MapLibreAdapter`. Toujours un `jumpTo` : cette carte n'anime jamais elle-même, elle suit. */
  syncCamera(camera: BackgroundMapCamera): void {
    this.map.jumpTo(camera);
  }

  /** Résout une fois que cette carte n'a plus aucune tuile en attente pour la vue courante (event
   * MapLibre "idle") — attend d'abord `ready` (fond initial appliqué, voir le constructeur), sinon
   * "idle" pourrait se déclencher avant même que `addBackgroundTileLayer` n'ait rien demandé à
   * charger. Voir `MapLibreAdapter.waitUntilIdle`, qui agrège celle-ci avec la carte principale. */
  async waitUntilIdle(): Promise<void> {
    await this.ready;
    await waitForMapIdle(this.map);
  }

  resize(): void {
    this.map.resize();
  }

  /** Recopie le fond tel qu'il est peint en ce moment dans `ctx` — première couche d'une capture
   * d'image (voir `MapLibreAdapter.captureImage`), avant le SVG et la carte principale. */
  async drawInto(ctx: CanvasRenderingContext2D, width: number, height: number): Promise<void> {
    await drawMapCanvasInto(this.map, ctx, width, height);
  }

  /** @internal Uniquement pour les tests. */
  getMapForTests(): MlMap {
    return this.map;
  }

  destroy(): void {
    this.map.remove();
    this.container.remove();
  }
}
